// `Momentum.chat.prefill(text)` — a widget drafts a message into the AI chat box. Nothing is
// sent: the person still reviews and presses send.
//
// The draft is kept in sessionStorage for a short while because getting to the chat box
// usually means a route change (home → a freshly created /chat/:id) that remounts the input
// once or twice. The input applies the draft whenever it mounts or resets while the draft is
// fresh, and clears it as soon as the person edits or sends.

export const CHAT_PREFILL_KEY = 'chat_prefill_draft';
export const CHAT_PREFILL_EVENT = 'chat:prefill';
export const CHAT_PREFILL_MAX_CHARS = 4000;
/** How long a draft stays claimable after a widget posted it. */
export const CHAT_PREFILL_TTL_MS = 30_000;

export interface ChatPrefillDetail { text: string; source?: string }
interface StoredDraft { text: string; at: number; source?: string }

export function requestChatPrefill(text: string, source?: string): void {
  const draft = text.trim().slice(0, CHAT_PREFILL_MAX_CHARS);
  if (!draft) return;
  const stored: StoredDraft = { text: draft, at: Date.now(), source };
  try { sessionStorage.setItem(CHAT_PREFILL_KEY, JSON.stringify(stored)); } catch { /* private mode — the event alone still works when chat is mounted */ }
  window.dispatchEvent(new CustomEvent<ChatPrefillDetail>(CHAT_PREFILL_EVENT, { detail: { text: draft, source } }));
}

/** The pending draft, if a widget posted one recently. Does not clear it — see clearChatPrefill. */
export function peekChatPrefill(): string | null {
  try {
    const raw = sessionStorage.getItem(CHAT_PREFILL_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredDraft>;
    if (typeof stored.text !== 'string' || typeof stored.at !== 'number' || Date.now() - stored.at > CHAT_PREFILL_TTL_MS) {
      sessionStorage.removeItem(CHAT_PREFILL_KEY);
      return null;
    }
    return stored.text || null;
  } catch {
    return null;
  }
}

/** Forget the draft — once the person has edited or sent it. */
export function clearChatPrefill(): void {
  try { sessionStorage.removeItem(CHAT_PREFILL_KEY); } catch { /* ignore */ }
}
